import Swiper from "swiper";
import {
  A11y,
  Autoplay,
  EffectFade,
  Navigation,
  Pagination,
} from "swiper/modules";

export default function initSliders(): void {
  Alpine.data("headerSlider", () => ({
    init() {
      const slides = Array.from(this.$root.querySelectorAll(".swiper-slide"));
      if (slides.length > 1) {
        (() =>
          new Swiper(this.$root, {
            modules: [EffectFade, Autoplay, Pagination],
            slidesPerView: 1,
            spaceBetween: 0,
            loop: true,
            effect: "fade",
            autoplay: {
              delay: 5000,
            },
            pagination: {
              el: this.$refs.pagination,
              clickable: true,
              bulletActiveClass:
                "swiper-pagination-bullet-active tw:w-8 tw:opacity-100",
            },
          }))();
      }
    },
  }));

  Alpine.data("categorySlider", () => ({
    swiper: null as Swiper | null,
    init() {
      if (window.matchMedia("(min-width: 768px)").matches) {
        this.initSwiper();
      }
      window.addEventListener("resize", () => {
        if (
          window.matchMedia("(min-width: 768px)").matches &&
          this.swiper === null
        ) {
          this.initSwiper();
        } else if (
          window.matchMedia("(max-width: 768px)").matches &&
          this.swiper !== null
        ) {
          this.swiper.destroy();
          this.swiper = null;
        }
      });
    },

    initSwiper() {
      this.swiper = new Swiper(this.$root, {
        modules: [Navigation, Pagination],
        slidesPerView: "auto",
        spaceBetween: 0,
        loop: false,
        grabCursor: true,
        navigation: {
          nextEl: this.$refs.next,
          prevEl: this.$refs.prev,
        },
        pagination: {
          el: this.$refs.pagination,
          clickable: true,
          bulletActiveClass:
            "swiper-pagination-bullet-active tw:w-8 tw:opacity-100",
        },
      });
    },
  }));

  Alpine.data("ctaSlider", () => ({
    swiper: null as Swiper | null,
    init() {
      if (window.matchMedia("(max-width: 1536px)").matches) {
        this.initSwiper();
      }
      window.addEventListener("resize", () => {
        if (
          window.matchMedia("(max-width: 1536px)").matches &&
          this.swiper === null
        ) {
          this.initSwiper();
        } else if (
          window.matchMedia("(min-width: 1536px)").matches &&
          this.swiper !== null
        ) {
          this.swiper.destroy();
          this.swiper = null;
        }
      });
    },

    initSwiper() {
      this.swiper = new Swiper(this.$root, {
        modules: [Navigation, Pagination],
        slidesPerView: "auto",
        spaceBetween: 0,
        loop: false,
        grabCursor: true,
        navigation: {
          nextEl: this.$refs.next,
          prevEl: this.$refs.prev,
        },
        pagination: {
          el: this.$refs.pagination,
          clickable: true,
          bulletActiveClass:
            "swiper-pagination-bullet-active tw:w-8 tw:opacity-100",
        },
      });
    },
  }));

  Alpine.data("productSlider", () => ({
    init() {
      const swiper = this.$root.querySelector(".swiper");
      if (swiper instanceof HTMLElement) {
        (() =>
          new Swiper(swiper, {
            modules: [Navigation, Pagination],
            slidesPerView: "auto",
            spaceBetween: 12,
            loop: false,
            grabCursor: true,
            navigation: {
              nextEl: this.$refs.next,
              prevEl: this.$refs.prev,
            },
            pagination: {
              el: this.$refs.pagination,
              clickable: true,
              bulletActiveClass:
                "swiper-pagination-bullet-active tw:w-8 tw:opacity-100",
            },
            breakpoints: {
              1280: {
                spaceBetween: 22,
              },
            },
          }))();

        const slides = Array.from(this.$root.querySelectorAll(".swiper-slide"));
        const productIds: string[] = [];
        Array.from(slides).forEach((slide) => {
          const productId = slide.getAttribute("data-product-id");
          if (productId && !productIds.includes(productId)) {
            productIds.push(productId);
          }
        });

        fetch(
          Craft.getActionUrl(
            "/actions/verwimp-portal/price/get-customer-prices",
            {
              customerId: window.Verwimp.currentCustomer.customerId,
              productIds: productIds.join(","),
            },
          ),
        )
          .then((response) => {
            if (!response.ok) {
              throw Error(response.statusText);
            }
            return response;
          })
          .then(async (response) => response.json())
          .then((data) => {
            if (data.success) {
              Object.keys(data.prices).forEach((productId) => {
                const slide = this.$root.querySelector(
                  `[data-product-id=${productId}]`,
                );
                if (slide instanceof HTMLElement) {
                  const customerPiceElement = slide.querySelector('.customer-price') as HTMLElement;
                  const customPrice = data.prices[productId];
                  if (
                    customerPiceElement.dataset.price && parseFloat(customerPiceElement.dataset
                      .price) > customPrice.customerPrice
                  ) {
                    const oldPrice = document.createElement('span');
                    oldPrice.classList.add('tw:text-sm/normal', 'tw:font-normal', 'tw:text-gray-400', 'tw:line-through', 'tw:hidden', 'tw:group-[.customer-price-mode]/body:flex');
                    oldPrice.textContent = customerPiceElement.textContent;
                    customerPiceElement.parentElement?.prepend(oldPrice);
                  }
                  customerPiceElement.textContent =
                    data.prices[productId].customerPriceFormatted ?? "";
                }
              });
            }
          })
          .catch(() => {
            productIds.forEach((productId) => {
              const slide = this.$root.querySelector(
                `[data-product-id=${productId}]`,
              );
              if (slide instanceof HTMLElement) {
                const customerPrice = slide.querySelector(
                  ".customer-price",
                ) as HTMLElement;
                const fragment = document.createDocumentFragment();
                const tooltipWrapper = document.createElement("div");
                tooltipWrapper.classList.add("tw:tooltip", "tw:group/tooltip");
                tooltipWrapper.innerHTML = "&#9888;";
                const tooltip = document.createElement("span");
                tooltip.classList.add(
                  "tw:tooltip-text",
                  "tw:group-hover/tooltip:visible",
                );
                tooltip.textContent = Craft.t(
                  "js",
                  "Dear customer, at the moment your specific prices cannot be loaded. We therefore show the gross purchase prices.",
                );
                tooltipWrapper.appendChild(tooltip);
                customerPrice.appendChild(fragment.appendChild(tooltipWrapper));
              }
            });
          });
      }
    },
  }));

  Alpine.data("uspSlider", () => ({
    swiper: null as Swiper | null,
    init() {
      if (window.matchMedia("(max-width: 1024px)").matches) {
        this.initSwiper();
      }
      window.addEventListener("resize", () => {
        if (
          window.matchMedia("(max-width: 1024px)").matches &&
          this.swiper === null
        ) {
          this.initSwiper();
        } else if (
          window.matchMedia("(min-width: 1024px)").matches &&
          this.swiper !== null
        ) {
          this.swiper.destroy();
          this.swiper = null;
        }
      });
    },

    initSwiper() {
      this.swiper = new Swiper(this.$root, {
        modules: [Autoplay],
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        autoplay: {
          delay: 5000,
        },
      });
    },
  }));

  Alpine.data("spotlightSlider", () => ({
    swiper: null as Swiper | null,
    init() {
      if (window.matchMedia("(max-width: 768px)").matches) {
        this.initSwiper();
      }
      window.addEventListener("resize", () => {
        if (
          window.matchMedia("(max-width: 768px)").matches &&
          this.swiper === null
        ) {
          this.initSwiper();
        } else if (
          window.matchMedia("(min-width: 768px)").matches &&
          this.swiper !== null
        ) {
          this.swiper.destroy();
          this.swiper = null;
        }
      });
    },

    initSwiper() {
      this.swiper = new Swiper(this.$root, {
        modules: [Navigation, Pagination],
        slidesPerView: "auto",
        spaceBetween: 0,
        loop: false,
        grabCursor: true,
        navigation: {
          nextEl: this.$refs.next,
          prevEl: this.$refs.prev,
        },
        pagination: {
          el: this.$refs.pagination,
          clickable: true,
          bulletActiveClass:
            "swiper-pagination-bullet-active tw:w-8 tw:opacity-100",
        },
      });
    },
  }));

  Alpine.data("brandsSlider", () => ({
    init() {
      const swiper = this.$root.querySelector(".swiper");
      if (swiper instanceof HTMLElement) {
        (() =>
          new Swiper(swiper, {
            modules: [A11y, Autoplay, Navigation],
            slidesPerView: "auto",
            spaceBetween: 8,
            loop: false,
            grabCursor: true,
            navigation: {
              nextEl: this.$refs.next,
              prevEl: this.$refs.prev,
            },
            autoplay: {
              delay: 3000,
            },
            a11y: {
              enabled: true,
            },
            breakpoints: {
              1280: {
                spaceBetween: 24,
              }
            }
          }))();
      }
    },
  }));
}
